import React, { useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import PageConfig from '@/component/layout/PageConfig.tsx'
import { TYPE_TopNav } from '@/component/layout/TopNav/TopNav.tsx'
import Footer from '@/component/layout/Footer/Footer.tsx'
import SalesModal from '@/pages/landing/Home/SalesModal.tsx'
import { useAuth } from '@/hooks/useAuth'
import CarearlyLogo from '@/assets/ic_carearly_logo.svg?react'
import DeviceImage from '@/assets/home_carearly_vest.jpg'
import ServiceImage from '@/assets/home_care_network_v3.jpg'
import CarearlyDashboardPreview from '@/component/CarearlyDashboardPreview.tsx'
import { getGuideCopy, type GuideStep } from '@/pages/landing/Guide/guideContent.ts'

const GuidePage: React.FunctionComponent = () => {
    const { i18n } = useTranslation()
    const copy = useMemo(
        () => getGuideCopy(i18n.resolvedLanguage || i18n.language),
        [i18n.resolvedLanguage, i18n.language]
    )
    const salesModalRef = useRef<any>(null)
    const navigate = useNavigate()
    const { isLoggedIn } = useAuth()

    const goToService = () => navigate(isLoggedIn ? '/dashboard' : '/login')

    const renderSteps = (steps: GuideStep[]) => (
        <div className="grid gap-[10px]">
            {steps.map(step => (
                <article key={step.number} className="rounded-[13px] border border-[#E2E7EE] bg-white p-[15px] shadow-[0_3px_12px_rgba(20,40,70,0.04)]">
                    <div className="flex items-start gap-[12px]">
                        <span className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-[9px] bg-[#EAF2FF] text-[11px] font-extrabold text-[#205FD8]">
                            {step.number}
                        </span>
                        <div>
                            <h3 className="text-[14px] font-extrabold text-[#22344D]">{step.title}</h3>
                            <p className="mt-[4px] text-[12px] leading-[1.65] text-[#6F7D91]">{step.description}</p>
                        </div>
                    </div>
                </article>
            ))}
        </div>
    )

    return (
        <>
            <PageConfig showTopNav typeTopNav={TYPE_TopNav.GUIDE}>
                <div className="bg-[#F5F8FC]">
                    <section className="border-b border-[#E4E9F0] bg-[radial-gradient(circle_at_82%_12%,#DCEBFF_0,transparent_36%),#F7FAFE] px-[24px] py-[80px] md:py-[48px] sm:px-[16px]">
                        <div className="section_1200">
                            <CarearlyLogo className="h-auto w-[180px]" />
                            <p className="mt-[26px] text-[12px] font-extrabold tracking-[0.16em] text-[#2D6DD4]">QUICK START GUIDE</p>
                            <h1 className="mt-[10px] text-[42px] font-extrabold tracking-[-0.045em] text-[#172B46] md:text-[31px]">
                                {copy.hero.titleLines[0]}<br />{copy.hero.titleLines[1]}
                            </h1>
                            <p className="mt-[18px] max-w-[650px] text-[15px] leading-[1.75] text-[#65758B]">
                                {copy.hero.description}
                            </p>
                            <nav className="app-scrollbar mt-[28px] flex max-w-full gap-[8px] overflow-x-auto pb-[4px]" aria-label={copy.nav.label}>
                                {copy.nav.items.map(({ id, label }) => (
                                    <a key={id} href={`#${id}`} className="app-subtle-button shrink-0 bg-white">{label}</a>
                                ))}
                            </nav>
                        </div>
                    </section>

                    <main className="section_1200 space-y-[84px] px-[24px] py-[76px] md:space-y-[58px] md:py-[48px] sm:px-[16px]">
                        <section id="wear" className="grid scroll-mt-[90px] grid-cols-[minmax(0,0.9fr)_minmax(420px,1.1fr)] items-center gap-[60px] lg:grid-cols-1 lg:gap-[28px]">
                            <div>
                                <p className="text-[12px] font-extrabold tracking-[0.14em] text-[#2D6DD4]">01 · DEVICE</p>
                                <h2 className="mt-[9px] text-[30px] font-extrabold tracking-[-0.035em] text-[#182C47] md:text-[25px]">{copy.wear.title}</h2>
                                <p className="mt-[14px] text-[14px] leading-[1.75] text-[#68778D]">
                                    {copy.wear.description}
                                </p>
                                <div className="mt-[22px] grid grid-cols-2 gap-[10px] sm:grid-cols-1">
                                    {copy.wear.points.map(({ title, description }) => (
                                        <div key={title} className="rounded-[12px] border border-[#E1E7EF] bg-white p-[14px]">
                                            <strong className="text-[13px] text-[#283B55]">{title}</strong>
                                            <p className="mt-[4px] text-[11px] leading-[1.6] text-[#768398]">{description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="flex min-h-[420px] items-center justify-center rounded-[24px] bg-[#EAF1FA] p-[28px] sm:min-h-[300px]">
                                <img src={DeviceImage} alt={copy.wear.imageAlt} className="max-h-[430px] w-full object-contain" />
                            </div>
                        </section>

                        <section id="dashboard" className="grid scroll-mt-[90px] grid-cols-[minmax(420px,1.15fr)_minmax(0,0.85fr)] items-center gap-[60px] lg:grid-cols-1 lg:gap-[28px]">
                            <div className="overflow-hidden rounded-[24px] border border-[#DDE4ED] bg-white p-[16px] shadow-[0_18px_50px_rgba(27,53,91,0.1)] lg:order-2">
                                <CarearlyDashboardPreview className="w-full" />
                            </div>
                            <div>
                                <p className="text-[12px] font-extrabold tracking-[0.14em] text-[#2D6DD4]">02 · MONITORING</p>
                                <h2 className="mt-[9px] text-[30px] font-extrabold tracking-[-0.035em] text-[#182C47] md:text-[25px]">{copy.dashboard.title}</h2>
                                <p className="mb-[20px] mt-[14px] text-[14px] leading-[1.75] text-[#68778D]">{copy.dashboard.description}</p>
                                {renderSteps(copy.dashboard.steps)}
                            </div>
                        </section>

                        <section id="response" className="scroll-mt-[90px] rounded-[24px] bg-[#172B46] px-[34px] py-[40px] text-white sm:px-[18px] sm:py-[26px]">
                            <div className="grid grid-cols-[0.65fr_1.35fr] gap-[44px] lg:grid-cols-1 lg:gap-[24px]">
                                <div>
                                    <p className="text-[12px] font-extrabold tracking-[0.14em] text-[#7FB0FF]">03 · RESPONSE</p>
                                    <h2 className="mt-[9px] text-[30px] font-extrabold tracking-[-0.035em] md:text-[25px]">{copy.response.title}</h2>
                                    <p className="mt-[14px] text-[13px] leading-[1.75] text-white/65">{copy.response.description}</p>
                                </div>
                                <div className="grid grid-cols-2 gap-[10px] sm:grid-cols-1">
                                    {copy.response.steps.map(step => (
                                        <article key={step.number} className="rounded-[14px] border border-white/10 bg-white/[0.07] p-[16px]">
                                            <span className="text-[11px] font-extrabold text-[#7FB0FF]">{step.number}</span>
                                            <h3 className="mt-[6px] text-[14px] font-bold">{step.title}</h3>
                                            <p className="mt-[5px] text-[11px] leading-[1.65] text-white/65">{step.description}</p>
                                        </article>
                                    ))}
                                </div>
                            </div>
                        </section>

                        <section id="users" className="grid scroll-mt-[90px] grid-cols-[minmax(0,0.9fr)_minmax(420px,1.1fr)] items-center gap-[60px] lg:grid-cols-1 lg:gap-[28px]">
                            <div>
                                <p className="text-[12px] font-extrabold tracking-[0.14em] text-[#2D6DD4]">04 · USERS</p>
                                <h2 className="mt-[9px] text-[30px] font-extrabold tracking-[-0.035em] text-[#182C47] md:text-[25px]">{copy.users.title}</h2>
                                <p className="mt-[14px] text-[14px] leading-[1.75] text-[#68778D]">
                                    {copy.users.description}
                                </p>
                                <ul className="mt-[20px] grid gap-[9px]">
                                    {copy.users.items.map(item => (
                                        <li key={item} className="flex items-center gap-[9px] text-[13px] font-semibold text-[#40516A]">
                                            <span className="flex h-[20px] w-[20px] items-center justify-center rounded-full bg-[#E8F5EF] text-[11px] text-[#16815F]">✓</span>
                                            {item}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                            <div className="flex min-h-[390px] items-center justify-center overflow-hidden rounded-[24px] bg-[#EAF1FA] p-[24px]">
                                <img src={ServiceImage} alt={copy.users.imageAlt} className="max-h-[420px] w-full object-contain" />
                            </div>
                        </section>

                        <section id="safe-zone" className="scroll-mt-[90px] rounded-[24px] border border-[#DDE6F2] bg-white px-[34px] py-[40px] shadow-[0_14px_42px_rgba(27,53,91,0.07)] sm:px-[18px] sm:py-[26px]">
                            <div className="grid grid-cols-[0.72fr_1.28fr] gap-[44px] lg:grid-cols-1 lg:gap-[24px]">
                                <div>
                                    <p className="text-[12px] font-extrabold tracking-[0.14em] text-[#2D6DD4]">05 · SAFE ZONE</p>
                                    <h2 className="mt-[9px] text-[30px] font-extrabold tracking-[-0.035em] text-[#182C47] md:text-[25px]">{copy.safeZone.title}</h2>
                                    <p className="mt-[14px] text-[13px] leading-[1.75] text-[#68778D]">
                                        {copy.safeZone.description}
                                    </p>
                                </div>
                                <div className="grid grid-cols-2 gap-[10px] sm:grid-cols-1">
                                    {copy.safeZone.steps.map(({ number, title, description }) => (
                                        <article key={number} className="rounded-[14px] border border-[#E1E7EF] bg-[#F8FAFD] p-[16px]">
                                            <span className="text-[11px] font-extrabold text-[#2D6DD4]">{number}</span>
                                            <h3 className="mt-[6px] text-[14px] font-extrabold text-[#2A3E59]">{title}</h3>
                                            <p className="mt-[5px] text-[11px] leading-[1.65] text-[#738197]">{description}</p>
                                        </article>
                                    ))}
                                </div>
                            </div>
                            <p className="mt-[18px] rounded-[12px] bg-[#FFF6E8] px-[14px] py-[11px] text-[11px] leading-[1.65] text-[#7E5A22]">
                                {copy.safeZone.notice}
                            </p>
                        </section>
                    </main>

                    <section className="border-t border-[#E2E8F0] bg-white px-[24px] py-[68px] text-center sm:px-[16px]">
                        <h2 className="text-[29px] font-extrabold tracking-[-0.035em] text-[#192D49] md:text-[24px]">{copy.cta.title}</h2>
                        <p className="mt-[10px] text-[13px] text-[#738196]">{copy.cta.description}</p>
                        <div className="mt-[24px] flex justify-center gap-[9px] sm:flex-col">
                            <button type="button" className="app-primary-button min-w-[170px]" onClick={goToService}>{copy.cta.goToService}</button>
                            <button type="button" className="app-subtle-button min-w-[170px]" onClick={() => salesModalRef.current?.show()}>{copy.cta.inquire}</button>
                        </div>
                    </section>
                </div>
                <Footer />
            </PageConfig>
            <SalesModal ref={salesModalRef} />
        </>
    )
}

export default GuidePage
